import { useEffect, useRef, useState } from "react";
import html2pdf from 'html2pdf.js';
import styled, { css } from "styled-components";
import { Modal, Button as AntButton } from "antd";
import { useTranslation } from "react-i18next";
import DownloadPDFButton from "../../../DownloadPDFButton";
import useAxios from "../../../../hooks/useAxios";
import { IExpense, IGetExpenseDetailList, IPdfExpense } from "../../../../models/financials.model";
import Loader from "../../../Loader";
import { getExpenseDetailList } from "../../../../api/financials.api";
import _ from "lodash";
import moment from "moment";
import { getCommonCodeLabel } from "../../../../lib/store";

interface IProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    selectedExpenseCodes: React.Key[],
    commonCode: string
}

const Block = styled.div`
    @font-face {
      font-family: "Roboto";
      font-weight: 100;
      src: url(../../../../public/fonts/Roboto-Thin.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Roboto";
      font-weight: 300;
      src: url(../../../../public/fonts//Roboto-Light.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Roboto";
      font-weight: 400;
      src: url(../../../../public/fonts/Roboto-Regular.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Roboto";
      font-weight: 500;
      src: url(../../../../public/fonts/Roboto-Medium.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Roboto";
      font-weight: 700;
      src: url(../../../../public/fonts/Roboto-Bold.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Roboto";
      font-weight: 900;
      src: url(../../../../public/fonts/Roboto-Black.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Noto Sans KR";
      font-weight: 100;
      src: url(../../../../public/fonts/NotoSansKR-Thin.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Noto Sans KR";
      font-weight: 200;
      src: url(../../../../public/fonts/NotoSansKR-ExtraLight.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Noto Sans KR";
      font-weight: 300;
      src: url(../../../../public/fonts/NotoSansKR-Light.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Noto Sans KR";
      font-weight: 400;
      src: url(../../../../public/fonts/NotoSansKR-Regular.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Noto Sans KR";
      font-weight: 500;
      src: url(../../../../public/fonts/NotoSansKR-Medium.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Noto Sans KR";
      font-weight: 600;
      src: url(../../../../public/fonts/NotoSansKR-SemiBold.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Noto Sans KR";
      font-weight: 700;
      src: url(../../../../public/fonts/NotoSansKR-Bold.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Noto Sans KR";
      font-weight: 800;
      src: url(../../../../public/fonts/NotoSansKR-ExtraBold.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    @font-face {
      font-family: "Noto Sans KR";
      font-weight: 900;
      src: url(../../../../public/fonts/NotoSansKR-Black.ttf) format("truetype");
      unicode-range: U+AC00-D7A3, U+0041-005A, u+0061-007a, u+0030-0039, u+0020-002f, u+003a-0040, u+005b-0060, u+007b-007e;
    }

    * {
      margin: 0;
      padding: 0;
      font-size: 14px;
      line-height: 1.2;
      box-sizing: border-box;
    }

    body {
      margin: 0 auto;
      height: 100%;
      
      font-family: "Roboto", "Noto Sans KR", sans-serif;
      font-weight: normal;
    }

    table {
      margin: 15mm 15mm 10mm 15mm;
      box-sizing: border-box;
      border-bottom: 1px solid #000;
      border-right: 1px solid #000;
      table-layout: fixed;
      border-collapse: collapse;
    }

    table colgroup col.col-style1 {
      width: 140px;
    }

    table colgroup col.col-style2 {
      width: 220px;
    }

    table th {
      background-color: #f7f7f7;
      border-left: 1px solid #000;
      border-top: 1px solid #000;
      padding: 10px 20px;
      font-weight: normal;
      line-height: 1.2;
      text-align: center;
    }

    table th.txt-type {
      line-height: 2;
    }

    table td {
      border-top: 1px solid #000;
      border-left: 1px solid #000;
      padding: 10px 20px;
      vertical-align: middle;
    }

    table td.content p {
      line-height: 1.56;
    }

    table td.content p.note {
      /*height: 220px;*/
      white-space:pre-wrap;
      overflow:hidden;
    }

    table td.content p.note.line10 {
      height: 280px;
      white-space:pre-wrap;
    }

    table td.content p.txt-guide {
      /*min-height: 380px;*/
      line-height: 2.56;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      color: #999;
    }

    table td.content p.txt-guide.line10 {
      min-height: 320px;
    }

    table tfoot td {
      padding: 20px;
    }

    table td p.area-bi {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    table td p.area-bi b {
      font-size: 18px;
    }

    table td:first-child {
      border-left: 1px solid #000;
    }

    table td.title {
      height: 100%;
      font-size: 40px;
      vertical-align: middle;
      text-align: center;
    }
`;
const cutPage = css`
    page-break-before: always;
`
const SCutPage = styled.div`
  ${cutPage}
`;

const PDF_CHUNK_SIZE = 10;

const MakePDFModal = ({visible, setVisible, selectedExpenseCodes, commonCode}: IProps) => {
    
    const contentRef = useRef<HTMLDivElement | null>(null);
    const { t, i18n } = useTranslation();

    const {data: expenseList, isLoading: isLoading1, error: error1, execute: executeList } = useAxios<IGetExpenseDetailList>();

    const [expenseRefineList, setExpenseRefineList] = useState<IPdfExpense[]>([]);

    useEffect(()=>{
        //selectedExpenseCodes로 Data 조회
        if(visible) {
            if(selectedExpenseCodes.length !== 0) {
                //파라미터 셋팅(form)
                const form = new FormData();
                form.append('expenseCodeList', selectedExpenseCodes.toString());
                
                executeList(getExpenseDetailList(form));
            }
        }
    },[selectedExpenseCodes, visible])

    useEffect(() => {
        if(expenseList) {
            let tempExpenseList : IPdfExpense[]  = _.cloneDeep(expenseList.data);

            if (tempExpenseList.length > 0) {
                tempExpenseList.forEach((item: IPdfExpense) => {
                    item.expenseDate = moment(item.expenseDate).format("YYYY-MM-DD");
                    item.expenseMtd = getCommonCodeLabel('FA0002', item.expenseMtd, i18n.language); 
                    item.expenseYn = getCommonCodeLabel('FA0003', item.expenseYn, i18n.language); 
                    item.clientBankCode = getCommonCodeLabel('SY0007', item.clientBankCode, i18n.language);
                    item.approvalStatus = getCommonCodeLabel('FA0005', item.approvalStatus, i18n.language);
                    item.proofType = getCommonCodeLabel('FA0004', item.proofType, i18n.language);
                    let numberOfLines = (item.summary.match(/\n/g) || []).length + 1;
                    if(numberOfLines > 10) {
                      numberOfLines = numberOfLines > 22 ? 22 : numberOfLines;
                      //기본크기 + (글자size * line-height * 10line 이후 rows)
                      item.summaryHeight = 220 + (14 * 1.56 * (numberOfLines - 10));
                      item.guideHeight = 380 - (14 * 1.56 * (numberOfLines - 10));
                    } else {
                      item.summaryHeight = 220;
                      item.guideHeight = 380;
                    }
                });
            }
            setExpenseRefineList(tempExpenseList);
        }    
    },[expenseList, isLoading1])

    const onSave = () => {
        const root = contentRef.current;
        if (!root) return;

        const chunks = root.querySelectorAll<HTMLElement>('.pdf-chunk');
        if (chunks.length === 0) return;

        const fileName = `${t('payment.expense.btn7')}`;

        const options = {
            filename: fileName,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: {
                unit: 'mm',
                format: 'a4',
                orientation: 'portrait',
            },
        };

        let worker = html2pdf().set(options).from(chunks[0]).toPdf();

        for (let i = 1; i < chunks.length; i++) {
            worker = worker
                .get('pdf')
                .then((pdf: { addPage: () => void }) => { pdf.addPage(); })
                .from(chunks[i])
                .toContainer()
                .toCanvas()
                .toPdf();
        }

        worker.save();
    }

    const getSummary = () => {
      if(i18n.language === 'ko') {
        return <>적<br />요</>;
      } else {
        return <>Summary</>;
      }
    }

    const getAttach = () => {
      if(i18n.language === 'ko') {
        return <>첨<br/>부<br/>파<br/>일</>;
      } else {
        return <>Attach</>;
      }
    }

    return(
        <Modal
            title={'PDF ' + t('student.student.button15')}
            width={847}
            open={visible}
            onCancel={() => setVisible(false)}
            footer={[
            <div className='comm-btn__area justify-content-center'>
                <AntButton
                    className='btn-basic pink'
                    onClick={onSave}
                >
                {t('student.student.button15')}
                </AntButton>
                <AntButton key="cancel" className='btn-basic gray' onClick={() => setVisible(false)}>
                {t('common.button.close')}
                </AntButton>
            </div>
            ]}
        >
        <div className="comm-bx__between" style={{marginBottom:'-50px', paddingRight:'56px'}}>
            <div></div>
            <div className="comm-btn__area right">
                <DownloadPDFButton onClick={onSave} btnText={t('student.student.button15')} />
            </div>
        </div>
        {isLoading1 ? <Loader /> : null}
        <div id="testId" ref={contentRef}>
            {_.chunk(expenseRefineList, PDF_CHUNK_SIZE).map((chunk, chunkIndex) => (
                <Block key={`pdf-chunk-${chunkIndex}`} className="pdf-chunk">
            {chunk.map((item, itemIndex) => (
                    <div key={`pdf-expense-${chunkIndex}-${itemIndex}`}>
                    <table>
                        <colgroup>
                            <col className="col-style1"/>
                            <col className="col-style1"/>
                            <col className="col-style1"/>
                            <col className="col-style1"/>
                            <col className="col-style2"/>
                        </colgroup>
                        <tbody>
                            <tr>
                                <td className="title" colSpan={3} rowSpan={5}>{t('payment.expense.btn7')}</td>
                                <th>{t('payment.expense.column1')}</th>
                                <td>{item.expenseDate}</td>
                            </tr>
                            <tr>
                                <th>{t('entrance.schedule.column29')}</th>
                                <td>{item.amount + ' ' + t('campus.setting.text2')}</td>
                            </tr>
                            <tr>
                                <th>{t('payment.expense.column4')}</th>
                                <td>{item.expenseMtd}</td>
                            </tr>
                            <tr>
                                <th>{t('payment.expense.label2')}</th>
                                <td>{item.proofType}</td>
                            </tr>
                            <tr>
                                <th>{t('payment.expense.label3')}</th>
                                <td>{item.proofDate}</td>
                            </tr>
                            <tr>
                                <th>{t('payment.expense.column5')}</th>
                                <td>{item.clientName}</td>
                                <th>{t('payment.expense.column6')}</th>
                                <td colSpan={2}>{item.clientDepositor}</td>
                            </tr>
                            <tr>
                                <th>{t('campus.shuttle.column12')}</th>
                                <td>{item.clientBankCode}</td>
                                <th>{t('campus.shuttle.column13')}</th>
                                <td colSpan={2}>{item.clientAccNo}</td>
                            </tr>
                            <tr>
                                <th>{t('common.column.title')}</th>
                                <td colSpan={4}>{item.title}</td>
                            </tr>
                            <tr>
                                <th className="txt-type">{getSummary()}</th>
                                <td colSpan={4} className="content">
                                    <p className={"note"} style={{height:item.summaryHeight + 'px'}}>
                                        {item.summary}
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <th className="txt-type">{getAttach()}</th>
                                <td colSpan={4} className="content">
                                <p className={"txt-guide"}  style={{height:item.guideHeight + 'px'}}>
                                    모든 거래에는 반드시 증빙이 있어야 합니다.<br />
                                    반드시 적격한 증빙을 여기에 부착해 주세요.
                                </p>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={5}>
                                <p className="area-bi">
                                    <b>{t('sms.send.text2')}({item.campusName})</b>
                                    <img src="/images/bi_canb1.png" style={{width:'160px'}} alt="캔비어 학원" />
                                </p>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                    <SCutPage />
                    </div>
            ))}
                </Block>
            ))}
        </div>
        </Modal>
    )
}

export default MakePDFModal;